'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Clock, Plus, Trash2, Wand2, Eye, EyeOff } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface PeriodTiming {
  id: string
  periodNumber: number
  startTime: string
  endTime: string
  isBreak: boolean
}

interface DayTimeSlots {
  [day: string]: {
    studyTimes: string[]
    restTime: string
    disabledSlots: Set<string> // Track which slots are disabled for this day
  }
}

interface PeriodTimingsDisplayProps {
  timetableData: any
  handleInputChange: (field: string, value: any) => void
  onValidationChange?: (isValid: boolean) => void
}

export function PeriodTimingsDisplay({
  timetableData,
  handleInputChange,
  onValidationChange,
}: PeriodTimingsDisplayProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.periodTimingsDisplay' })
  const [periodCount, setPeriodCount] = useState<number>(
    timetableData.periodCount || 8
  )
  const [periodTimings, setPeriodTimings] = useState<PeriodTiming[]>(
    timetableData.periodTimings || []
  )
  const [showAutoGenerateDialog, setShowAutoGenerateDialog] = useState(false)
  const [showTableView, setShowTableView] = useState(false)
  const [dayTimeSlots, setDayTimeSlots] = useState<DayTimeSlots>({})

  const [autoGenSettings, setAutoGenSettings] = useState({
    startTime: timetableData.startTime || '08:00',
    endTime: timetableData.endTime || '17:00',
    breakStartTime: '12:00',
    breakEndTime: '13:00',
    periodLength: timetableData.periodLength || 45,
  })

  // Normalize incoming schoolDays (they may be localized labels or English names) to canonical ids
  const normalizedSchoolDays = useMemo(() => {
    const dayIds = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const idToEnglish: Record<string, string> = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    }

    const inputDays: string[] = Array.isArray(timetableData.schoolDays) ? timetableData.schoolDays : []
    const result: string[] = []

    const tryResolveId = (value: string): string | null => {
      if (!value) return null
      const raw = String(value).trim()
      const lower = raw.toLowerCase()
      if (dayIds.includes(lower)) return lower
      for (const id of dayIds) {
        if (
          raw === idToEnglish[id] ||
          raw === t('days.' + id) ||
          raw === t('timetableForm.days.' + id)
        ) {
          return id
        }
      }
      return null
    }

    inputDays.forEach((d) => {
      const id = tryResolveId(d)
      if (id && !result.includes(id)) result.push(id)
    })

    if (result.length === 0) {
      return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    }
    return result
  }, [timetableData.schoolDays, t])

  // Ensure parent data is normalized to ids to avoid future duplication
  useEffect(() => {
    const current = Array.isArray(timetableData.schoolDays) ? timetableData.schoolDays : []
    const isSame =
      current.length === normalizedSchoolDays.length &&
      current.every((d: string, i: number) => d === normalizedSchoolDays[i])
    if (!isSame) {
      handleInputChange('schoolDays', normalizedSchoolDays)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSchoolDays])

  const schoolDays = normalizedSchoolDays

  // Initialize/sync day time slots when school days change (preserve existing toggles, add missing days)
  useEffect(() => {
    setDayTimeSlots((prev) => {
      const next: DayTimeSlots = { ...prev }
      schoolDays.forEach((dayId: string) => {
        if (!next[dayId]) {
          next[dayId] = {
            studyTimes: [],
            restTime: '',
            disabledSlots: new Set(),
          }
        }
      })
      // Optionally, we could prune days not in schoolDays; skipping to preserve any previous data
      return next
    })
  }, [schoolDays])

  // Automatically disable all break periods by default (but preserve user-enabled breaks)
  // Use a ref to track which breaks were explicitly enabled by the user
  const enabledBreaksRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (periodTimings.length === 0) return

    setDayTimeSlots((prev) => {
      const updated: DayTimeSlots = { ...prev }
      const formatTimeForServer = (time: string) => {
        if (!time) return ''
        const [hours, minutes] = time.split(':').map(Number)
        return minutes === 0 ? hours.toString() : time
      }

      // Get all break time slots
      const breakSlots = periodTimings
        .filter((period) => period.isBreak && period.startTime && period.endTime)
        .map((period) => {
          const startFormatted = formatTimeForServer(period.startTime)
          const endFormatted = formatTimeForServer(period.endTime)
          return `${startFormatted} => ${endFormatted}`
        })
        .filter((timeStr) => timeStr !== ' => ')

      // For each school day, ensure breaks are disabled by default
      schoolDays.forEach((dayId: string) => {
        if (!updated[dayId]) {
          updated[dayId] = {
            studyTimes: [],
            restTime: '',
            disabledSlots: new Set(),
          }
        }

        // Add all break slots to disabledSlots by default
        // But preserve breaks that were explicitly enabled by the user
        const currentDisabled = new Set(updated[dayId].disabledSlots)
        breakSlots.forEach((breakSlot) => {
          // Create a unique key for this break on this day
          const breakKey = `${dayId}:${breakSlot}`
          
          // If user explicitly enabled this break, don't disable it
          if (enabledBreaksRef.current.has(breakKey)) {
            // User enabled it, so remove from disabledSlots
            currentDisabled.delete(breakSlot)
          } else {
            // Not explicitly enabled, so disable by default
            currentDisabled.add(breakSlot)
          }
        })
        updated[dayId] = {
          ...updated[dayId],
          disabledSlots: currentDisabled,
        }
      })

      return updated
    })
  }, [periodTimings, schoolDays])

  // Validate period timings
  const isValidPeriodTimings = useMemo(() => {
    // Check if at least one period has both startTime and endTime filled
    const hasValidPeriods = periodTimings.some(
      (period) =>
        period.startTime &&
        period.endTime &&
        period.startTime.trim() !== '' &&
        period.endTime.trim() !== ''
    )

    // Also check that periodCount is at least 1
    const hasValidCount = periodCount >= 1

    return hasValidPeriods && hasValidCount
  }, [periodTimings, periodCount])

  // Report validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValidPeriodTimings)
    }
  }, [isValidPeriodTimings, onValidationChange])

  // Update parent component when period timings change
  useEffect(() => {
    handleInputChange('periodTimings', periodTimings)
    handleInputChange('periodCount', periodCount)

    // Create the timeSlots object in the format expected by the server
    const timeSlots: Record<
      string,
      { studyTimes: string[]; restTime: string }
    > = {}

    // Format time as "8 => 9" or "8:00 => 9:00" based on input format
    const formatTimeForServer = (time: string) => {
      if (!time) return ''
      const [hours, minutes] = time.split(':').map(Number)
      return minutes === 0 ? hours.toString() : time
    }

    // Get regular periods and break periods
    const regularPeriods = periodTimings.filter((period) => !period.isBreak)
    const breakPeriods = periodTimings.filter((period) => period.isBreak)

    regularPeriods.sort((a, b) => a.periodNumber - b.periodNumber)

    // Process each day
    schoolDays.forEach((dayId: string) => {
      const daySlots = dayTimeSlots[dayId]
      const disabledSlots = daySlots?.disabledSlots || new Set()

      // Get study times for this day (excluding disabled slots)
      const studyTimes = regularPeriods
        .filter((period) => {
          if (!period.startTime || !period.endTime) return false
          const timeSlot = `${formatTimeForServer(period.startTime)} => ${formatTimeForServer(period.endTime)}`
          return !disabledSlots.has(timeSlot)
        })
        .map((period) => {
          const startFormatted = formatTimeForServer(period.startTime)
          const endFormatted = formatTimeForServer(period.endTime)
          return `${startFormatted} => ${endFormatted}`
        })
        .filter((timeStr) => timeStr !== ' => ')

      // Get rest time (use first break that's not disabled)
      let restTime = ''
      if (breakPeriods.length > 0) {
        const firstBreak = breakPeriods[0]
        if (firstBreak.startTime && firstBreak.endTime) {
          const breakTimeStr = `${formatTimeForServer(firstBreak.startTime)} => ${formatTimeForServer(firstBreak.endTime)}`
          if (!disabledSlots.has(breakTimeStr)) {
            restTime = breakTimeStr
          }
        }
      }

      timeSlots[dayId] = {
        studyTimes,
        restTime,
      }
    })

    // Update the timeslots in the parent component
    handleInputChange('dayTimeSlots', dayTimeSlots);
  }, [periodTimings, periodCount, dayTimeSlots, handleInputChange])

  // Check if a time slot is a break period
  const isBreakSlot = (timeSlot: string): boolean => {
    const formatTimeForServer = (time: string) => {
      if (!time) return ''
      const [hours, minutes] = time.split(':').map(Number)
      return minutes === 0 ? hours.toString() : time
    }

    return periodTimings.some((period) => {
      if (!period.isBreak || !period.startTime || !period.endTime) return false
      const periodSlot = `${formatTimeForServer(period.startTime)} => ${formatTimeForServer(period.endTime)}`
      return periodSlot === timeSlot
    })
  }

  // Toggle time slot for a specific day
  const toggleTimeSlot = (dayId: string, timeSlot: string) => {
    setDayTimeSlots((prev) => {
      const updated = { ...prev }
      if (!updated[dayId]) {
        updated[dayId] = {
          studyTimes: [],
          restTime: '',
          disabledSlots: new Set(),
        }
      }

      const disabledSlots = new Set(updated[dayId].disabledSlots)
      const breakKey = `${dayId}:${timeSlot}`
      const isBreak = isBreakSlot(timeSlot)
      
      if (disabledSlots.has(timeSlot)) {
        // Enabling the slot (removing from disabled)
        disabledSlots.delete(timeSlot)
        // If it's a break, track that user explicitly enabled it
        if (isBreak) {
          enabledBreaksRef.current.add(breakKey)
        }
      } else {
        // Disabling the slot (adding to disabled)
        disabledSlots.add(timeSlot)
        // If it's a break, remove from enabled breaks tracking
        if (isBreak) {
          enabledBreaksRef.current.delete(breakKey)
        }
      }

      updated[dayId] = {
        ...updated[dayId],
        disabledSlots,
      }

      return updated
    })
  }

  // Get all unique time slots from periods
  const getAllTimeSlots = () => {
    const slots: Array<{
      slot: string
      isBreak: boolean
      period: PeriodTiming
    }> = []

    periodTimings.forEach((period) => {
      if (period.startTime && period.endTime) {
        const formatTimeForServer = (time: string) => {
          if (!time) return ''
          const [hours, minutes] = time.split(':').map(Number)
          return minutes === 0 ? hours.toString() : time
        }

        const timeSlot = `${formatTimeForServer(period.startTime)} => ${formatTimeForServer(period.endTime)}`
        slots.push({
          slot: timeSlot,
          isBreak: period.isBreak,
          period,
        })
      }
    })

    return slots.sort((a, b) => a.period.periodNumber - b.period.periodNumber)
  }

  // Check if a time slot is disabled for a specific day
  const isSlotDisabled = (dayId: string, timeSlot: string) => {
    return dayTimeSlots[dayId]?.disabledSlots?.has(timeSlot) || false
  }

  // Generate a new period timing
  const addNewPeriod = () => {
    const newPeriod: PeriodTiming = {
      id: uuidv4(),
      periodNumber: periodTimings.length + 1,
      startTime: '',
      endTime: '',
      isBreak: false,
    }
    setPeriodTimings([...periodTimings, newPeriod])
  }

  // Remove a period timing
  const removePeriod = (id: string) => {
    const updatedPeriods = periodTimings.filter((period) => period.id !== id)
    const renumberedPeriods = updatedPeriods.map((period, index) => ({
      ...period,
      periodNumber: index + 1,
    }))
    setPeriodTimings(renumberedPeriods)
  }

  // Update a period timing
  const updatePeriod = (id: string, field: keyof PeriodTiming, value: any) => {
    const updatedPeriods = periodTimings.map((period) => {
      if (period.id === id) {
        return { ...period, [field]: value }
      }
      return period
    })
    setPeriodTimings(updatedPeriods)
  }

  // Auto-generate period timings
  const autoGeneratePeriods = () => {
    const { startTime, endTime, breakStartTime, breakEndTime, periodLength } =
      autoGenSettings

    const convertToMinutes = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours * 60 + (minutes || 0)
    }

    const startMinutes = convertToMinutes(startTime)
    const endMinutes = convertToMinutes(endTime)
    const breakStartMinutes = convertToMinutes(breakStartTime)
    const breakEndMinutes = convertToMinutes(breakEndTime)

    const newPeriods: PeriodTiming[] = []
    let currentMinutes = startMinutes
    let periodNumber = 1

    const formatTimeForUI = (minutes: number) => {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
    }

    // Add periods before break
    while (currentMinutes + periodLength <= breakStartMinutes) {
      const periodEndMinutes = currentMinutes + periodLength

      newPeriods.push({
        id: uuidv4(),
        periodNumber: periodNumber++,
        startTime: formatTimeForUI(currentMinutes),
        endTime: formatTimeForUI(periodEndMinutes),
        isBreak: false,
      })

      currentMinutes = periodEndMinutes
    }

    // Add break period
    newPeriods.push({
      id: uuidv4(),
      periodNumber: periodNumber++,
      startTime: breakStartTime,
      endTime: breakEndTime,
      isBreak: true,
    })

    // Add periods after break
    currentMinutes = breakEndMinutes
    while (
      currentMinutes + periodLength <= endMinutes &&
      newPeriods.length < periodCount
    ) {
      const periodEndMinutes = currentMinutes + periodLength

      newPeriods.push({
        id: uuidv4(),
        periodNumber: periodNumber++,
        startTime: formatTimeForUI(currentMinutes),
        endTime: formatTimeForUI(periodEndMinutes),
        isBreak: false,
      })

      currentMinutes = periodEndMinutes
    }

    setPeriodTimings(newPeriods)
    setPeriodCount(newPeriods.length)
    setShowAutoGenerateDialog(false)

    // Reset disabled slots when regenerating
    const resetDaySlots: DayTimeSlots = {}
    schoolDays.forEach((day: string) => {
      resetDaySlots[day] = {
        studyTimes: [],
        restTime: '',
        disabledSlots: new Set(),
      }
    })
    setDayTimeSlots(resetDaySlots)
  }

  // Update period count
  const handlePeriodCountChange = (value: number) => {
    setPeriodCount(value)

    if (periodTimings.length < value) {
      const newPeriods = [...periodTimings]
      for (let i = periodTimings.length; i < value; i++) {
        newPeriods.push({
          id: uuidv4(),
          periodNumber: i + 1,
          startTime: '',
          endTime: '',
          isBreak: false,
        })
      }
      setPeriodTimings(newPeriods)
    } else if (periodTimings.length > value) {
      setPeriodTimings(periodTimings.slice(0, value))
    }
  }

  const allTimeSlots = getAllTimeSlots()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Clock className="mr-2 h-5 w-5" />
          {t('title')}
        </CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs
          value={showTableView ? 'table' : 'periods'}
          onValueChange={(value) => setShowTableView(value === 'table')}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="periods">{t('tabs.periodSetup')}</TabsTrigger>
            <TabsTrigger value="table">{t('tabs.dayByDayView')}</TabsTrigger>
          </TabsList>

          <TabsContent value="periods" className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="space-y-2 flex-1 w-full sm:w-auto">
                <Label htmlFor="periodCount">{t('periods.numberOfPeriods')}</Label>
                <Input
                  id="periodCount"
                  type="number"
                  min="1"
                  value={periodCount}
                  onChange={(e) =>
                    handlePeriodCountChange(
                      Number.parseInt(e.target.value) || 1
                    )
                  }
                  className="min-h-[44px]"
                />
              </div>

              <Dialog
                open={showAutoGenerateDialog}
                onOpenChange={setShowAutoGenerateDialog}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" className="mb-0.5 min-h-[44px] w-full sm:w-auto">
                    <Wand2 className="mr-2 h-4 w-4" />
                    {t('periods.autoGenerate')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-[95vw] md:w-full max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{t('autoGenerate.title')}</DialogTitle>
                    <DialogDescription>
                      {t('autoGenerate.description')}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="startTime">{t('autoGenerate.schoolStartTime')}</Label>
                        <Input
                          id="startTime"
                          type="time"
                          value={autoGenSettings.startTime}
                          onChange={(e) =>
                            setAutoGenSettings({
                              ...autoGenSettings,
                              startTime: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="endTime">{t('autoGenerate.schoolEndTime')}</Label>
                        <Input
                          id="endTime"
                          type="time"
                          value={autoGenSettings.endTime}
                          onChange={(e) =>
                            setAutoGenSettings({
                              ...autoGenSettings,
                              endTime: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="breakStartTime">{t('autoGenerate.breakStartTime')}</Label>
                        <Input
                          id="breakStartTime"
                          type="time"
                          value={autoGenSettings.breakStartTime}
                          onChange={(e) =>
                            setAutoGenSettings({
                              ...autoGenSettings,
                              breakStartTime: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="breakEndTime">{t('autoGenerate.breakEndTime')}</Label>
                        <Input
                          id="breakEndTime"
                          type="time"
                          value={autoGenSettings.breakEndTime}
                          onChange={(e) =>
                            setAutoGenSettings({
                              ...autoGenSettings,
                              breakEndTime: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="periodLength">
                        {t('autoGenerate.periodLength')}
                      </Label>
                        <Input
                          id="periodLength"
                          type="number"
                          min="0"
                          value={autoGenSettings.periodLength}
                          onChange={(e) =>
                            setAutoGenSettings({
                              ...autoGenSettings,
                              periodLength: Number.parseInt(e.target.value) || 45,
                            })
                          }
                          className="min-h-[44px]"
                        />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setShowAutoGenerateDialog(false)}
                    >
                      {t('autoGenerate.cancel')}
                    </Button>
                    <Button onClick={autoGeneratePeriods}>
                      {t('autoGenerate.generatePeriods')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button variant="outline" onClick={addNewPeriod} className="min-h-[44px] w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                {t('periods.addPeriod')}
              </Button>
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription>
                {t('periods.configureSchedule')}
              </AlertDescription>
            </Alert>

            <ScrollArea className="h-[400px] border rounded-md p-4">
              {periodTimings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t('periods.noPeriodsDefined')}
                </div>
              ) : (
                <div className="space-y-4">
                  {periodTimings.map((period) => (
                    <div
                      key={period.id}
                      className={`p-4 rounded-md border ${period.isBreak ? 'bg-amber-50' : 'bg-slate-50'}`}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center">
                          <span className="font-medium mr-2">
                            {t('periods.period', { number: period.periodNumber })}
                          </span>
                          {period.isBreak ? (
                            <Badge
                              variant="secondary"
                              className="bg-amber-100 text-amber-800"
                            >
                              {t('periods.break')}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-800"
                            >
                              {t('periods.lesson')}
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removePeriod(period.id)}
                          className="h-10 w-10 min-h-[44px] min-w-[44px] text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`start-${period.id}`}>
                            {t('periods.startTime')}
                          </Label>
                          <Input
                            id={`start-${period.id}`}
                            type="time"
                            value={period.startTime}
                            onChange={(e) =>
                              updatePeriod(
                                period.id,
                                'startTime',
                                e.target.value
                              )
                            }
                            className="min-h-[44px]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`end-${period.id}`}>{t('periods.endTime')}</Label>
                          <Input
                            id={`end-${period.id}`}
                            type="time"
                            value={period.endTime}
                            onChange={(e) =>
                              updatePeriod(period.id, 'endTime', e.target.value)
                            }
                            className="min-h-[44px]"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mt-4">
                        <Switch
                          id={`break-${period.id}`}
                          checked={period.isBreak}
                          onCheckedChange={(checked) =>
                            updatePeriod(period.id, 'isBreak', checked)
                          }
                        />
                        <Label htmlFor={`break-${period.id}`}>
                          {t('periods.thisIsBreakPeriod')}
                        </Label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="table" className="space-y-6">
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription>
                {t('dayByDayView.description')}
              </AlertDescription>
            </Alert>

            {allTimeSlots.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('dayByDayView.noTimeSlots')}
              </div>
            ) : (
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px] min-w-[150px]">{t('dayByDayView.timeSlot')}</TableHead>
                      <TableHead className="w-[100px] min-w-[80px]">{t('dayByDayView.type')}</TableHead>
                       {schoolDays.map((dayId: string) => (
                        <TableHead
                           key={dayId}
                          className="text-center min-w-[100px]"
                        >
                           {t('days.' + dayId)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allTimeSlots.map(({ slot, isBreak }) => (
                      <TableRow key={slot}>
                        <TableCell className="font-medium">{slot}</TableCell>
                        <TableCell>
                          {isBreak ? (
                            <Badge
                              variant="secondary"
                              className="bg-amber-100 text-amber-800"
                            >
                              {t('periods.break')}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-800"
                            >
                              {t('periods.lesson')}
                            </Badge>
                          )}
                        </TableCell>
                        {schoolDays.map((dayId: string) => {
                          const isDisabled = isSlotDisabled(dayId, slot)
                          return (
                            <TableCell
                              key={`${dayId}-${slot}`}
                              className="text-center"
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleTimeSlot(dayId, slot)}
                                className={`h-10 w-10 min-h-[44px] min-w-[44px] ${
                                  isDisabled
                                    ? 'text-red-500 hover:text-red-700 hover:bg-red-50'
                                    : 'text-green-500 hover:text-green-700 hover:bg-green-50'
                                }`}
                              >
                                {isDisabled ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-green-500" />
                  <span>{t('dayByDayView.enabled')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <EyeOff className="h-4 w-4 text-red-500" />
                  <span>{t('dayByDayView.disabled')}</span>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
